/**
 * Scout v0 — Synonym confirmation step (P16, task 2).
 *
 * For LOOSE roles, the AI proposes equivalent titles (reason + evidence +
 * confidence). The operator confirms / edits / rejects each proposal. The
 * confirmed set is displayed and becomes the ConfirmedRoleEdit[] that flows
 * into the confirm endpoint.
 *
 * EXACT roles are shown as already-confirmed (no proposals, no AI call).
 */

import { useEffect, useMemo, useState } from 'react';
import type { ComposedCriteria, ConfirmedRoleEdit, RoleProposals, SynonymProposal } from '@/types/scout';
import { useToast } from '@/components/ui/useToast';
import { usePropose } from '@/lib/scout-queries';
import { ErrorBanner, LoadingSpinner } from '@/components/ui';
import { getErrorMessage } from '@/lib/queries';

interface SynonymConfirmProps {
  /** The composed spec (from the compose step). */
  composed: ComposedCriteria;
  /**
   * Called with the confirmed edits whenever the operator changes them. The
   * parent owns persistence (it passes these to the confirm endpoint along with
   * the template id) — this component only produces the confirmed set.
   */
  onEditsChange: (edits: ConfirmedRoleEdit[]) => void;
  /** Called with whether all loose roles are confirmed (ready for preview). */
  onAllConfirmedChange: (allConfirmed: boolean) => void;
  /** Whether the propose/confirm step is disabled (e.g. launch in flight). */
  disabled?: boolean;
}

/**
 * Per-role confirmation state. For each loose role, the operator builds a
 * confirmed set of equivalent titles (initially the AI proposals).
 */
interface RoleConfirmState {
  roleTitle: string;
  matchMode: 'exact' | 'loose';
  /** The titles the operator has confirmed (edited list). */
  confirmedTitles: string[];
  /** The raw AI proposals (for display + reject). */
  proposals: SynonymProposal[];
  /** Whether this role has been explicitly resolved (confirmed or rejected). */
  resolved: boolean;
}

export function SynonymConfirm({
  composed,
  onEditsChange,
  onAllConfirmedChange,
  disabled,
}: SynonymConfirmProps) {
  const propose = usePropose();
  const { addToast } = useToast();

  const [proposals, setProposals] = useState<RoleProposals[] | null>(null);
  const [proposeError, setProposeError] = useState<string | null>(null);
  const [hasProposed, setHasProposed] = useState(false);

  // Per-role confirmed-title state, keyed by lowercased role title. Exact
  // roles are seeded as already-confirmed at mount — `composed` never changes
  // while this step is mounted (the parent unmounts it when recomposing).
  const [roleStates, setRoleStates] = useState<Map<string, RoleConfirmState>>(() => {
    const initial = new Map<string, RoleConfirmState>();
    for (const role of composed.resolvedRoles) {
      if (role.matchMode !== 'exact') continue;
      initial.set(role.title.toLowerCase(), {
        roleTitle: role.title,
        matchMode: 'exact',
        confirmedTitles: role.resolvedTitles,
        proposals: [],
        resolved: true,
      });
    }
    return initial;
  });

  const looseRoles = useMemo(
    () => composed.resolvedRoles.filter((r) => r.matchMode === 'loose'),
    [composed],
  );
  const exactRoles = useMemo(
    () => composed.resolvedRoles.filter((r) => r.matchMode === 'exact'),
    [composed],
  );

  // Determine if there are loose roles needing proposals.
  const needsProposals = looseRoles.some((r) => r.needsProposals === true);
  const hasLooseRoles = looseRoles.length > 0;

  // Emit the confirmed edits + all-confirmed flag up to the parent.
  useEffect(() => {
    const edits: ConfirmedRoleEdit[] = [];
    let allConfirmed = true;

    for (const role of composed.resolvedRoles) {
      if (role.matchMode !== 'loose') continue;
      // Loose roles with persisted synonyms are already operator-confirmed on the
      // backend (needsProposals !== true). They ride through unchanged — nothing
      // for the operator to resolve, and no edit to send.
      if (role.needsProposals !== true) continue;

      const key = role.title.toLowerCase();
      const state = roleStates.get(key);

      if (!state || !state.resolved) {
        allConfirmed = false;
        continue;
      }
      // The confirmed titles (minus the role title itself, which is always retained).
      const synonyms = state.confirmedTitles.filter(
        (t) => t.toLowerCase() !== role.title.toLowerCase(),
      );
      edits.push({ title: role.title, resolvedTitles: synonyms });
    }

    onEditsChange(edits);
    onAllConfirmedChange(allConfirmed);
  }, [roleStates, composed, onEditsChange, onAllConfirmedChange]);

  const handlePropose = () => {
    setProposeError(null);
    propose.mutate(
      { composed },
      {
        onSuccess: (data) => {
          setProposals(data.proposals);
          setHasProposed(true);

          // Seed each loose role's confirmed titles with the AI proposals.
          setRoleStates((prev) => {
            const next = new Map(prev);
            for (const rp of data.proposals) {
              const key = rp.roleTitle.toLowerCase();
              const proposedTitles = rp.proposals.map((p) => p.title);
              next.set(key, {
                roleTitle: rp.resolvedTitle,
                matchMode: 'loose',
                confirmedTitles: proposedTitles,
                proposals: rp.proposals,
                resolved: false,
              });
            }
            return next;
          });

          if (data.proposals.length === 0) {
            addToast('info', 'No loose roles needed synonyms.');
          } else {
            addToast('success', `Proposed synonyms for ${data.proposals.length} role(s).`);
          }
        },
        onError: (err) => {
          setProposeError(getErrorMessage(err));
          addToast('error', getErrorMessage(err));
        },
      },
    );
  };

  const toggleProposal = (roleTitle: string, proposalTitle: string) => {
    const key = roleTitle.toLowerCase();
    setRoleStates((prev) => {
      const next = new Map(prev);
      const state = next.get(key);
      if (!state) return prev;
      const has = state.confirmedTitles.some((t) => t.toLowerCase() === proposalTitle.toLowerCase());
      const updated = has
        ? state.confirmedTitles.filter((t) => t.toLowerCase() !== proposalTitle.toLowerCase())
        : [...state.confirmedTitles, proposalTitle];
      next.set(key, { ...state, confirmedTitles: updated });
      return next;
    });
  };

  const addCustomTitle = (roleTitle: string, title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const key = roleTitle.toLowerCase();
    setRoleStates((prev) => {
      const next = new Map(prev);
      const state = next.get(key);
      if (!state) return prev;
      if (state.confirmedTitles.some((t) => t.toLowerCase() === trimmed.toLowerCase())) return prev;
      next.set(key, { ...state, confirmedTitles: [...state.confirmedTitles, trimmed] });
      return next;
    });
  };

  const removeTitle = (roleTitle: string, title: string) => {
    const key = roleTitle.toLowerCase();
    setRoleStates((prev) => {
      const next = new Map(prev);
      const state = next.get(key);
      if (!state) return prev;
      next.set(key, {
        ...state,
        confirmedTitles: state.confirmedTitles.filter(
          (t) => t.toLowerCase() !== title.toLowerCase(),
        ),
      });
      return next;
    });
  };

  const confirmRole = (roleTitle: string) => {
    const key = roleTitle.toLowerCase();
    setRoleStates((prev) => {
      const next = new Map(prev);
      const state = next.get(key);
      if (!state) return prev;
      next.set(key, { ...state, resolved: true });
      return next;
    });
  };

  const rejectRole = (roleTitle: string) => {
    // Reject = no synonyms, just the role title itself.
    const key = roleTitle.toLowerCase();
    setRoleStates((prev) => {
      const next = new Map(prev);
      const state = next.get(key);
      if (!state) return prev;
      next.set(key, { ...state, confirmedTitles: [], resolved: true });
      return next;
    });
  };

  return (
    <section className="card" aria-labelledby="synonym-heading">
      <div className="mb-3">
        <h2 id="synonym-heading" className="text-lg font-semibold text-text">
          Step 2 - confirm synonyms
        </h2>
        <p className="text-xs text-muted mt-0.5">
          For loose roles, review AI-proposed equivalent titles. Confirm, edit, or reject each.
        </p>
      </div>

      {/* No loose roles — nothing to confirm. */}
      {!hasLooseRoles && (
        <p className="text-sm text-muted py-2">
          No loose roles in this spec. All roles are exact (no AI proposals needed).
        </p>
      )}

      {/* Exact roles summary. */}
      {exactRoles.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-muted mb-1">Exact roles (used as-is, no confirmation needed):</p>
          <ul className="flex flex-wrap gap-1.5">
            {exactRoles.map((role) => (
              <li key={role.title} className="badge badge-primary">
                {role.title}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Loose roles needing proposals. */}
      {hasLooseRoles && needsProposals && !hasProposed && (
        <div className="space-y-3">
          <p className="text-sm text-text">
            {looseRoles.length} loose role{looseRoles.length === 1 ? '' : 's'} need synonym proposals.
          </p>
          <button
            type="button"
            className="btn-primary"
            onClick={handlePropose}
            disabled={propose.isPending || disabled}
          >
            {propose.isPending ? 'Proposing...' : 'Propose synonyms (AI)'}
          </button>
          {proposeError && <ErrorBanner message={proposeError} retryable={propose.error?.retryable} onRetry={handlePropose} />}
        </div>
      )}

      {/* Loading proposals. */}
      {propose.isPending && <LoadingSpinner className="py-4" />}

      {/* Proposal results. */}
      {proposals && proposals.length > 0 && (
        <ul className="space-y-4">
          {proposals.map((rp) => {
            const key = rp.roleTitle.toLowerCase();
            const state = roleStates.get(key);
            const confirmedTitles = state?.confirmedTitles ?? [];
            const resolved = state?.resolved ?? false;

            return (
              <li key={rp.roleTitle} className="border border-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-text">{rp.resolvedTitle}</span>
                    <span className="badge badge-secondary ml-2">loose</span>
                  </div>
                  {resolved && (
                    <span className="badge badge-primary" aria-label="Confirmed">
                      ✓ confirmed
                    </span>
                  )}
                </div>

                {/* Proposals with reason + confidence. */}
                <ul className="space-y-2 mb-3">
                  {rp.proposals.map((p) => {
                    const checked = confirmedTitles.some(
                      (t) => t.toLowerCase() === p.title.toLowerCase(),
                    );
                    return (
                      <li key={p.title} className="flex items-start gap-2">
                        <label className="flex items-start gap-2 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            className="mt-1 shrink-0"
                            checked={checked}
                            onChange={() => toggleProposal(rp.roleTitle, p.title)}
                            disabled={disabled}
                          />
                          <div className="min-w-0">
                            <p className="text-sm text-text">
                              <span className="font-medium">{p.title}</span>{' '}
                              <span className="text-xs text-muted">
                                (confidence: {(p.confidence * 100).toFixed(0)}%)
                              </span>
                            </p>
                            {p.reason && <p className="text-xs text-muted mt-0.5">{p.reason}</p>}
                            {p.evidence?.quote && (
                              <p className="text-xs text-muted italic mt-0.5">"{p.evidence.quote}"</p>
                            )}
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>

                {/* Confirmed set display. */}
                <div className="mb-2">
                  <p className="text-xs text-muted mb-1">Confirmed set:</p>
                  {confirmedTitles.length === 0 ? (
                    <p className="text-sm text-muted italic">(none - role title only)</p>
                  ) : (
                    <ul className="flex flex-wrap gap-1.5">
                      {confirmedTitles.map((t) => (
                        <li
                          key={t}
                          className="badge badge-primary flex items-center gap-1"
                        >
                          {t}
                          {!disabled && (
                            <button
                              type="button"
                              className="text-xs opacity-70 hover:opacity-100"
                              onClick={() => removeTitle(rp.roleTitle, t)}
                              aria-label={`Remove ${t}`}
                            >
                              ×
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Add custom title. */}
                {!disabled && (
                  <CustomTitleInput onAdd={(title) => addCustomTitle(rp.roleTitle, title)} />
                )}

                {/* Confirm / reject actions. */}
                {!resolved && !disabled && (
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => confirmRole(rp.roleTitle)}
                    >
                      Confirm set
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-warning"
                      onClick={() => rejectRole(rp.roleTitle)}
                    >
                      Reject (role title only)
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Loose roles that already had persisted synonyms (no propose needed). */}
      {hasLooseRoles && !needsProposals && (
        <div className="space-y-2">
          <p className="text-sm text-muted">
            Loose roles with previously-confirmed synonyms (no new proposals needed):
          </p>
          <ul className="space-y-1">
            {looseRoles
              .filter((r) => r.needsProposals !== true)
              .map((role) => (
                <li key={role.title} className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-text">{role.title}</span>
                  <span className="badge badge-primary">confirmed</span>
                  <span className="text-muted">
                    {role.resolvedTitles.filter((t) => t.toLowerCase() !== role.title.toLowerCase()).length} synonym(s)
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Custom title input                                                      */
/* ─────────────────────────────────────────────────────────────────────── */

function CustomTitleInput({ onAdd }: { onAdd: (title: string) => void }) {
  const [value, setValue] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    onAdd(value);
    setValue('');
  };
  return (
    <form onSubmit={submit} className="flex gap-2 items-center">
      <input
        className="input flex-1 text-sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add a custom equivalent title"
      />
      <button type="submit" className="btn-ghost px-3 py-1.5 text-sm" disabled={!value.trim()}>
        Add
      </button>
    </form>
  );
}